interface OptionalFlowProps {
    hasPaymentMethods?: boolean;
    hasDefaultPaymentMethod?: boolean;
    hasSecondaryPaymentMethod?: boolean;
    resolveDefaults?: boolean;
    isAutoPay?: boolean;
    creditChecked?: boolean;
    idProductType?: number
    skipPayors?: boolean;
    enroll?: boolean;
    update?: boolean;
    amount?: number;
    cancel?: boolean;
    addDefaultPM?: boolean;
    missingAccounts?: boolean;
}

export type Step = string | string[] | { path: string; optional?: (overrides: Record<string, unknown>) => boolean | undefined };

export interface Flow {
    id: string;
    steps: Step[];
}

export const Flows = {
    updateEnrolledInvoice: {
        id: `updateEnrolledInvoice`,
        steps: [
            `/guarantor`,
            `/guarantor/invoice/payors`,
            `/guarantor`
        ]
    },

    setupSingle: {
        id: `setupSingle`,
        steps: [
            `/signup`,
            `/welcome`,
            `/information-safety`,
            {
                path: `/existing-payment-methods`,
                optional: ({ hasPaymentMethods, hasDefaultPaymentMethod }: OptionalFlowProps) => !hasPaymentMethods || hasDefaultPaymentMethod
            },
            {
                path: `/payment-method-selector`,
                optional: ({ hasDefaultPaymentMethod }: OptionalFlowProps) => hasDefaultPaymentMethod
            },
            {
                path: `/default-payment-method`,
                optional: ({ hasDefaultPaymentMethod }: OptionalFlowProps) => hasDefaultPaymentMethod
            },
            {
                path: `/setup-autopay`,
                optional: ({ isAutoPay }: OptionalFlowProps) => isAutoPay
            },
            `/`
        ]
    },
    setupMultiple: {
        id: `setupMultiple`,
        steps: [
            `/signup`,
            `/welcome`,
            `/information-safety`,
            `/guarantor/multiple-accounts-info`,
            {
                path: `/existing-payment-methods`,
                optional: ({ hasPaymentMethods }: OptionalFlowProps) => !hasPaymentMethods
            },
            {
                path: `/payment-method-selector`,
                // TODO: type
                optional: ({ missingAccounts }: OptionalFlowProps) => !missingAccounts
            },
            {
                path: `/default-payment-method`,
                // TODO: type
                optional: ({ missingAccounts }: OptionalFlowProps) => !missingAccounts
            },
            `/setup-autopay`,
            `/`
        ]
    },
    makePayment: {
        id: `makePayment`,
        steps: [
            [`/guarantor`, `/payor`, `/payment-method-settings`],
            {
                path: `/payment-method-selector`,
                optional: ({ hasDefaultPaymentMethod, addDefaultPM }: OptionalFlowProps) => hasDefaultPaymentMethod && addDefaultPM !== true
            },
            {
                path: `/default-payment-method`,
                optional: ({ hasDefaultPaymentMethod, addDefaultPM }: OptionalFlowProps) => hasDefaultPaymentMethod && addDefaultPM !== true
            },
            {
                path: `/make-payment`,
                optional: ({ amount }: OptionalFlowProps) => !!amount
            },
            `/confirm-payment`,
            {
                path: `/resolve-defaults`,
                optional: ({ resolveDefaults }: OptionalFlowProps) => !resolveDefaults
            },
            `/`
        ]
    },

    addDefaultPaymentMethod: {
        id: `addDefaultPaymentMethod`,
        steps: [
            `/payment-method-settings`,
            `/payment-method-selector`,
            `/default-payment-method`,
            {
                path: `/resolve-defaults`,
                optional: ({ resolveDefaults }: OptionalFlowProps) => !resolveDefaults
            },
            `/payment-method-settings`
        ]
    },
    sendPasswordReset: {
        id: `sendPasswordReset`,
        steps: [
            [`/login`, `/signup`],
            `/forgot-password`,
            `/forgot-password-info`,
            `/`
        ]
    },
    resendWelcome: {
        id: `resendWelcome`,
        steps: [
            `/signup`,
            `/resend-welcome`,
            `/resend-welcome-info`,
            `/`
        ]
    }
} as const satisfies Record<string, Flow>;

export const Help = {
    Phone: `623-263-9162`,
    Email: `help@sunbound.ai`,
    FAQ: `https://www.sunbound.ai/faq`,
    FAQErrors: `https://help.sunbound.ai/hc/en-us/articles/23208159704091-Handling-Errors`,
    AutoPay: `https://help.sunbound.ai/hc/en-us/articles/18384985410587`,
    Privacy: `https://www.sunbound.ai/privacy-policy`,
    PaymentDay: `https://help.sunbound.ai/hc/en-us/articles/18384985410587#01HZMQEKA7QWYY8ZFN8ASRENVH`
};

export const Errors = {
    INTERNAL_ERROR: `Please contact system administrator`,
    PM_REGISTRATION: `There was an issue registering your payment method. Please verify your payment information and try again. {0}`,
    ACH_VALIDATION_FAILED: `We couldn't verify that bank account. Please double-check the routing and account numbers, then try again.`,
    PAYMENT_PLATFORM_ERROR: `Your payment could not be completed due to an unexpected error. Please try again later.`

};

export const System = {
    MIN_PAYMENT_AMOUNT: 5.0
};

export const IMG_BUCKET = `https://images.sunboundhomes.com`;

// Remove if review passes(don't know if other way is actually better)
// export const FailedTransactions = {
//     R01: `Please resolve and try again, or try again with a different payment method.`,
//     R02: `Please try again with a different payment method`,
//     R03: `Please double check your account number, routing number, and account holder name before reconnecting this payment method and trying again, or try again with a different payment method.`,
//     R04: `Please double check your account number before reconnecting this payment method and trying again, or try again with a different payment method.`,
//     R05: `Please try again with a different payment method`,
//     R06: `Please try again with a different payment method, or contact Sunbound support`,
//     R07: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R08: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R09: `Please resolve and try again, or try again with a different payment method.`,
//     R10: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R11: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R12: `Please contact your financial institution as you may need to update your account and routing numbers, then try again, or try with a different payment method`,
//     R13: `Please double check your routing number before reconnecting this payment method and trying again, or try again with a different payment method.`,
//     R14: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R15: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R16: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R17: `Please contact your bank to see if this was due to a block on transactions. If not, please double check your account information before reconnecting this payment method and trying again. You may also try again with a different payment method`,
//     R20: `Please try again with a different payment method`,
//     R21: `Please try again with a different payment method`,
//     R22: `Please try again with a different payment method`,
//     R23: `Please contact your bank to see why the refund was refused, then contact Sunbound support to discuss options for completing your refund.`,
//     R24: `Please contact Sunbound support.`,
//     R29: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R31: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R33: `Please try again with a different payment method`
// };

export const FailedTransactionsActions = [
    {
        statusCodes: [`R01`, `R09`],
        action: `Please resolve and try again, or try again with a different payment method.`
    },
    {
        statusCodes: [`R02`, `R05`, `R20`, `R21`, `R22`, `R33`],
        action: `Please try again with a different payment method.`
    },
    {
        statusCodes: [`R03`],
        action: `Please double check your account number, routing number, and account holder name before reconnecting this payment method and trying again, or try again with a different payment method.`
    },
    {
        statusCodes: [`R04`],
        action: `Please double check your account number before reconnecting this payment method and trying again, or try again with a different payment method.`
    },
    {
        statusCodes: [`R06`],
        action: `Please try again with a different payment method, or contact Sunbound support.`
    },
    {
        statusCodes: [`R07`, `R08`, `R10`, `R11`, `R14`, `R15`, `R16`, `R29`, `R31`],
        action: `Please contact your bank about removing the block on transactions, or try again with a different payment method.`
    },
    {
        statusCodes: [`R12`],
        action: `Please contact your financial institution as you may need to update your account and routing numbers, then try again, or try with a different payment method.`
    },
    {
        statusCodes: [`R13`],
        action: `Please double check your routing number before reconnecting this payment method and trying again, or try again with a different payment method.`
    },
    {
        statusCodes: [`R17`],
        action: `Please contact your bank to see if this was due to a block on transactions. If not, please double check your account information before reconnecting this payment method and trying again. You may also try again with a different payment method.`
    },
    {
        statusCodes: [`R23`],
        action: `Please contact your bank to see why the refund was refused, then contact Sunbound support to discuss options for completing your refund.`
    },
    {
        statusCodes: [`R24`],
        action: `Please contact Sunbound support.`
    }
];
